import { useState, useMemo, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth, subMonths, addMonths, startOfDay, endOfDay, parseISO, eachDayOfInterval, eachWeekOfInterval, startOfWeek, endOfWeek, isSameDay } from "date-fns";
import { nl, fr, de, enUS } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CalendarIcon, Loader2, TrendingUp, Users, Euro, Briefcase } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

type Period = "this_month" | "prev_month" | "next_month" | "custom";

interface Reservation {
  id: string;
  date: string;
  status: string;
  price: number;
  duration: number;
  service_id: string;
  customer_id: string;
}

interface Service {
  id: string;
  name: string;
}

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2, 173 58% 39%))",
  "hsl(var(--chart-3, 43 74% 66%))",
  "hsl(var(--chart-4, 27 87% 67%))",
  "hsl(var(--chart-5, 12 76% 61%))",
  "hsl(var(--accent))",
];

const StatisticsPage = () => {
  const { t, language } = useLanguage();
  const { profile } = useAuth();
  const orgId = profile?.organisation_id;

  const [period, setPeriod] = useState<Period>("this_month");
  const [customFrom, setCustomFrom] = useState<Date | undefined>();
  const [customTo, setCustomTo] = useState<Date | undefined>();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  const dateFnsLocale = language === "nl" ? nl : language === "fr" ? fr : language === "de" ? de : enUS;

  const { from, to } = useMemo(() => {
    const now = new Date();
    if (period === "this_month") return { from: startOfMonth(now), to: endOfMonth(now) };
    if (period === "prev_month") {
      const prev = subMonths(now, 1);
      return { from: startOfMonth(prev), to: endOfMonth(prev) };
    }
    if (period === "next_month") {
      const next = addMonths(now, 1);
      return { from: startOfMonth(next), to: endOfMonth(next) };
    }
    return { from: customFrom || startOfMonth(now), to: customTo || endOfMonth(now) };
  }, [period, customFrom, customTo]);

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      setLoading(true);
      const [resRes, svcRes] = await Promise.all([
        supabase
          .from("reservations")
          .select("id, date, status, price, duration, service_id, customer_id")
          .eq("organisation_id", orgId)
          .gte("date", startOfDay(from).toISOString())
          .lte("date", endOfDay(to).toISOString()),
        supabase
          .from("services")
          .select("id, name")
          .eq("organisation_id", orgId),
      ]);
      setReservations(resRes.data || []);
      setServices(svcRes.data || []);
      setLoading(false);
    };
    load();
  }, [orgId, from, to]);

  // Derived stats
  const activeReservations = reservations.filter((r) => r.status !== "cancelled");
  const cancelledReservations = reservations.filter((r) => r.status === "cancelled");
  const totalRevenue = activeReservations.reduce((s, r) => s + (r.price || 0), 0);
  const uniqueCustomers = new Set(activeReservations.map((r) => r.customer_id)).size;

  // Reservations per day (bar chart)
  const days = eachDayOfInterval({ start: from, end: to });
  const dailyData = days.map((day) => ({
    date: format(day, "dd MMM", { locale: dateFnsLocale }),
    [t("stats.bookings")]: activeReservations.filter((r) => isSameDay(parseISO(r.date), day)).length,
    [t("stats.cancelled")]: cancelledReservations.filter((r) => isSameDay(parseISO(r.date), day)).length,
  }));

  // Revenue per week (line chart)
  const weeks = eachWeekOfInterval({ start: from, end: to }, { weekStartsOn: 1 });
  const weeklyRevenue = weeks.map((weekStart) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    const weekLabel = `${format(weekStart, "dd MMM", { locale: dateFnsLocale })}`;
    const rev = activeReservations
      .filter((r) => { const d = parseISO(r.date); return d >= weekStart && d <= weekEnd; })
      .reduce((s, r) => s + (r.price || 0), 0);
    return { week: weekLabel, [t("stats.revenue")]: Number(rev.toFixed(2)) };
  });

  // Services distribution (pie chart)
  const serviceMap = new Map(services.map((s) => [s.id, s.name]));
  const serviceCountMap = new Map<string, number>();
  activeReservations.forEach((r) => {
    const name = serviceMap.get(r.service_id) || "Unknown";
    serviceCountMap.set(name, (serviceCountMap.get(name) || 0) + 1);
  });
  const servicePieData = Array.from(serviceCountMap.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Status breakdown (pie chart)
  const statusCountMap = new Map<string, number>();
  reservations.forEach((r) => {
    const label = t(`stats.status_${r.status}`) || r.status;
    statusCountMap.set(label, (statusCountMap.get(label) || 0) + 1);
  });
  const statusPieData = Array.from(statusCountMap.entries()).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">{t("stats.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("stats.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(["prev_month", "this_month", "next_month", "custom"] as Period[]).map((p) => (
            <Button
              key={p}
              variant={period === p ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(p)}
            >
              {t(`stats.period_${p}`)}
            </Button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="flex gap-3 items-center flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("w-[160px] justify-start text-left font-normal", !customFrom && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customFrom ? format(customFrom, "PP", { locale: dateFnsLocale }) : t("stats.from")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={customFrom} onSelect={setCustomFrom} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          <span className="text-muted-foreground text-sm">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("w-[160px] justify-start text-left font-normal", !customTo && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customTo ? format(customTo, "PP", { locale: dateFnsLocale }) : t("stats.to")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={customTo} onSelect={setCustomTo} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard icon={TrendingUp} label={t("stats.total_bookings")} value={activeReservations.length.toString()} />
            <KPICard icon={Euro} label={t("stats.total_revenue")} value={`€${totalRevenue.toFixed(2)}`} />
            <KPICard icon={Users} label={t("stats.unique_customers")} value={uniqueCustomers.toString()} />
            <KPICard icon={Briefcase} label={t("stats.cancellations")} value={cancelledReservations.length.toString()} />
          </div>

          {/* Reservations per day */}
          <div className="rounded-xl border border-border bg-card p-5 shadow-card">
            <h2 className="text-sm font-semibold text-card-foreground mb-4">{t("stats.bookings_per_day")}</h2>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" }} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} />
                  <Legend />
                  <Bar dataKey={t("stats.bookings")} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey={t("stats.cancelled")} fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Revenue per week */}
          <div className="rounded-xl border border-border bg-card p-5 shadow-card">
            <h2 className="text-sm font-semibold text-card-foreground mb-4">{t("stats.revenue_per_week")}</h2>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyRevenue} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" }} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} formatter={(value: number) => `€${value.toFixed(2)}`} />
                  <Line type="monotone" dataKey={t("stats.revenue")} stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pie charts row */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5 shadow-card">
              <h2 className="text-sm font-semibold text-card-foreground mb-4">{t("stats.by_service")}</h2>
              <div className="h-[260px]">
                {servicePieData.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center pt-20">{t("stats.no_data")}</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={servicePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false} fontSize={11} stroke="hsl(var(--card))">
                        {servicePieData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" }} itemStyle={{ color: "hsl(var(--popover-foreground))" }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-5 shadow-card">
              <h2 className="text-sm font-semibold text-card-foreground mb-4">{t("stats.by_status")}</h2>
              <div className="h-[260px]">
                {statusPieData.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center pt-20">{t("stats.no_data")}</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false} fontSize={11} stroke="hsl(var(--card))">
                        {statusPieData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" }} itemStyle={{ color: "hsl(var(--popover-foreground))" }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KPICard = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
  <div className="rounded-xl border border-border bg-card p-5 shadow-card">
    <div className="flex items-center gap-3 mb-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
    </div>
    <p className="text-2xl font-bold text-card-foreground">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
  </div>
);

export default StatisticsPage;
